import { useState, useRef, useCallback, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ImagePlus, X, Loader2, Star } from "lucide-react";
import {
  useEventImages,
  useUploadImage,
  useDeleteImage,
  useSetCoverImage,
} from "@/hooks/useImages";
import { toast } from "sonner";
import { ImagePreview } from "@/components/ui/image-preview";
import {
  emptyOverlays,
  mergeImageOverlays,
  type ImageOverlays,
} from "@/components/images/imageOverlays";

interface EventImageManagerProps {
  eventId: string;
  disabled?: boolean;
  eventsBasePath?: string;
}

export function EventImageManager({ eventId, disabled, eventsBasePath }: EventImageManagerProps) {
  const { data: fetchedImages, isLoading: imagesLoading } = useEventImages(eventId, eventsBasePath);
  const uploadImage = useUploadImage();
  const deleteImage = useDeleteImage();
  const setCoverImage = useSetCoverImage();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [overlays, setOverlays] = useState<ImageOverlays>(emptyOverlays);
  const [uploadingCount, setUploadingCount] = useState(0);

  const displayImages = useMemo(
    () => mergeImageOverlays(fetchedImages, overlays),
    [fetchedImages, overlays],
  );
  const hasCover = displayImages.some((img) => img.isCover);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;

    e.target.value = "";

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const shouldBeCover = !hasCover && i === 0 && displayImages.length === 0;

      setUploadingCount((c) => c + 1);
      try {
        const result = await uploadImage.mutateAsync({
          file,
          entityType: "event",
          entityId: eventId,
          isCover: shouldBeCover,
          sortOrder: displayImages.length + i,
        });
        setOverlays((o) => ({ ...o, added: [...o.added, result.image] }));
      } catch (err) {
        toast.error(`Nie udało się przesłać zdjęcia: ${file.name}`, {
          description: err instanceof Error ? err.message : undefined,
        });
      } finally {
        setUploadingCount((c) => c - 1);
      }
    }
  };

  const handleDelete = useCallback(
    async (imageId: string) => {
      setOverlays((o) => {
        const deletedIds = new Set(o.deletedIds);
        deletedIds.add(imageId);
        return { ...o, deletedIds };
      });
      try {
        await deleteImage.mutateAsync(imageId);
      } catch (err) {
        setOverlays((o) => {
          const deletedIds = new Set(o.deletedIds);
          deletedIds.delete(imageId);
          return { ...o, deletedIds };
        });
        toast.error("Nie udało się usunąć zdjęcia", {
          description: err instanceof Error ? err.message : undefined,
        });
      }
    },
    [deleteImage],
  );

  const handleSetCover = useCallback(
    async (imageId: string) => {
      const previousCoverId = overlays.coverId;
      setOverlays((o) => ({ ...o, coverId: imageId }));
      try {
        await setCoverImage.mutateAsync(imageId);
      } catch (err) {
        setOverlays((o) => ({ ...o, coverId: previousCoverId }));
        toast.error("Nie udało się ustawić okładki", {
          description: err instanceof Error ? err.message : undefined,
        });
      }
    },
    [setCoverImage, overlays.coverId],
  );

  return (
    <div className="space-y-2">
      <Label>Zdjęcia</Label>
      <div className="flex flex-wrap gap-3">
        {displayImages.map((img) => (
          <div key={img.id} className="relative group">
            <ImagePreview src={img.variantUrls.thumb} alt="Zdjęcie wydarzenia" />
            {img.isCover ? (
              <span className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] text-center py-0.5 rounded-b">
                Okładka
              </span>
            ) : (
              <button
                type="button"
                disabled={disabled || setCoverImage.isPending}
                onClick={() => handleSetCover(img.id)}
                title="Ustaw jako okładkę"
                className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] text-center py-0.5 rounded-b opacity-0 group-hover:opacity-100 focus:opacity-100 group-focus-within:opacity-100 transition-opacity flex items-center justify-center gap-0.5"
              >
                <Star className="h-2.5 w-2.5" />
                Okładka
              </button>
            )}
            <button
              type="button"
              disabled={disabled || deleteImage.isPending}
              onClick={() => handleDelete(img.id)}
              aria-label="Usuń zdjęcie"
              className="absolute -top-1.5 -right-1.5 bg-destructive text-destructive-foreground rounded-full p-0.5 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus:opacity-100 transition-opacity"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
        {Array.from({ length: uploadingCount }).map((_, i) => (
          <div
            key={`uploading-${i}`}
            className="flex items-center justify-center rounded overflow-hidden shrink-0 bg-muted"
            style={{ width: 70, height: 70 }}
          >
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ))}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        className="hidden"
        onChange={handleFileSelect}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled || imagesLoading || uploadImage.isPending}
        onClick={() => fileInputRef.current?.click()}
      >
        <ImagePlus className="h-4 w-4 mr-2" />
        Dodaj zdjęcie
      </Button>
    </div>
  );
}
